import { useEffect, useRef, useState } from 'react';
import { uploadMediaFile } from '@/hooks/useUpload';

// ─────────────────────────────────────────────────────────────
// Éditeur de blog riche — images, vidéos, liens, tailles et
// alignements, insérables n'importe où dans le texte.
//
// Le contenu est stocké en HTML dans `blog_articles.content`.
// Les anciens articles en texte brut sont convertis à l'ouverture.
// ─────────────────────────────────────────────────────────────

interface BlogRichEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
}

type Align = 'left' | 'center' | 'right';
type MediaSize = 'small' | 'medium' | 'full';

const SIZE_WIDTH: Record<MediaSize, string> = {
  small: '40%',
  medium: '70%',
  full: '100%',
};

/** Convertit un contenu texte historique (## titres, - puces) en HTML. */
export function legacyTextToHtml(raw: string): string {
  const src = String(raw || '');
  if (!src.trim()) return '';
  // Contenu déjà HTML : on ne touche à rien.
  if (/<(p|div|h[1-6]|img|figure|ul|ol|blockquote|iframe|video)\b/i.test(src)) return src;
  return src
    .split(/\n{2,}/)
    .map((block) => {
      const lines = block.split('\n').map((l) => l.trim()).filter(Boolean);
      if (lines.length === 0) return '';
      if (lines.every((l) => l.startsWith('- ') || l.startsWith('* '))) {
        return `<ul>${lines.map((l) => `<li>${escapeHtml(l.slice(2))}</li>`).join('')}</ul>`;
      }
      return lines
        .map((line) => {
          if (line.startsWith('### ')) return `<h3>${escapeHtml(line.slice(4))}</h3>`;
          if (line.startsWith('## ') || line.startsWith('# ')) {
            return `<h2>${escapeHtml(line.replace(/^#{1,2}\s+/, ''))}</h2>`;
          }
          if (line.startsWith('> ')) return `<blockquote>${escapeHtml(line.slice(2))}</blockquote>`;
          return `<p>${escapeHtml(line)}</p>`;
        })
        .join('');
    })
    .join('');
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Extrait un identifiant de vidéo YouTube / Vimeo d'une URL. */
function videoEmbed(url: string): { type: 'iframe' | 'video'; src: string } | null {
  const clean = url.trim();
  if (!clean) return null;
  const yt = clean.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{6,})/i);
  if (yt) return { type: 'iframe', src: `https://www.youtube.com/embed/${yt[1]}` };
  const vimeo = clean.match(/vimeo\.com\/(?:video\/)?(\d+)/i);
  if (vimeo) return { type: 'iframe', src: `https://player.vimeo.com/video/${vimeo[1]}` };
  if (/\.(mp4|webm|ogg)(\?|$)/i.test(clean)) return { type: 'video', src: clean };
  return null;
}

export default function BlogRichEditor({ value, onChange, placeholder }: BlogRichEditorProps) {
  const ref = useRef<HTMLDivElement>(null);
  const lastSent = useRef('');
  const savedRange = useRef<Range | null>(null);
  const [mediaPanel, setMediaPanel] = useState<'image' | 'video' | null>(null);
  const [mediaUrl, setMediaUrl] = useState('');
  const [mediaSize, setMediaSize] = useState<MediaSize>('medium');
  const [mediaAlign, setMediaAlign] = useState<Align>('center');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [inserted, setInserted] = useState<string[]>([]);
  const [activeBlock, setActiveBlock] = useState<'p' | 'h2' | 'h3'>('p');

  // Initialise le contenu sans écraser la saisie en cours.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const next = legacyTextToHtml(value);
    if (next === lastSent.current) return;
    if (el.innerHTML !== next) el.innerHTML = next;
    lastSent.current = value;
  }, [value]);

  const emit = () => {
    const el = ref.current;
    if (!el) return;
    // Un bloc vide ne doit pas générer de <br> parasite.
    if (!el.textContent?.trim() && !el.querySelector('img,iframe,video,figure')) {
      el.innerHTML = '';
    }
    const html = el.innerHTML;
    lastSent.current = html;
    onChange(html);
  };

  const focusEditor = () => ref.current?.focus();

  // Conserve la dernière position du curseur DANS l'éditeur. Sans cela, cliquer
  // sur la barre d'outils déplace la sélection hors de la zone de texte et
  // l'image partirait à la fin de l'article.
  const rememberRange = () => {
    const el = ref.current;
    const sel = window.getSelection();
    if (!el || !sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    if (el.contains(range.commonAncestorContainer)) {
      savedRange.current = range.cloneRange();
    }
  };

  const restoreRange = (): Range | null => {
    const el = ref.current;
    if (!el) return null;
    const stored = savedRange.current;
    if (stored && el.contains(stored.commonAncestorContainer)) {
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(stored);
      return stored;
    }
    return null;
  };

  const exec = (command: string, arg?: string) => {
    focusEditor();
    restoreRange();
    document.execCommand(command, false, arg);
    emit();
  };

  const formatBlock = (tag: 'p' | 'h2' | 'h3' | 'blockquote') => {
    exec('formatBlock', tag);
    if (tag === 'p' || tag === 'h2' || tag === 'h3') setActiveBlock(tag);
  };

  const insertNode = (node: Node) => {
    const el = ref.current;
    if (!el) return;
    el.focus();
    const sel = window.getSelection();
    let range: Range | null = null;
    if (sel && sel.rangeCount > 0 && el.contains(sel.anchorNode)) {
      range = sel.getRangeAt(0);
    } else if (savedRange.current && el.contains(savedRange.current.commonAncestorContainer)) {
      range = savedRange.current;
    }

    const after = document.createElement('p');
    after.innerHTML = '<br>';

    if (range) {
      range.deleteContents();
      range.insertNode(node);
      node.parentNode?.insertBefore(after, node.nextSibling);
      range.setStart(after, 0);
      range.collapse(true);
      sel?.removeAllRanges();
      sel?.addRange(range);
      savedRange.current = range.cloneRange();
    } else {
      el.appendChild(node);
      el.appendChild(after);
      const next = document.createRange();
      next.setStart(after, 0);
      next.collapse(true);
      sel?.removeAllRanges();
      sel?.addRange(next);
      savedRange.current = next.cloneRange();
    }
    emit();
  };

  const buildFigure = (inner: string): HTMLElement => {
    const fig = document.createElement('figure');
    fig.className = 'blog-figure';
    fig.setAttribute('data-align', mediaAlign);
    fig.setAttribute('data-size', mediaSize);
    fig.setAttribute('style', `width:${SIZE_WIDTH[mediaSize]};margin:${mediaAlign === 'center' ? '1.5rem auto' : mediaAlign === 'right' ? '1.5rem 0 1.5rem auto' : '1.5rem auto 1.5rem 0'};`);
    fig.innerHTML = inner;
    return fig;
  };

  const insertImage = (src: string) => {
    const alt = window.prompt('Texte alternatif de l’image (facultatif)') || '';
    insertNode(buildFigure(`<img src="${src}" alt="${escapeHtml(alt)}" />`));
    setInserted((prev) => [...prev, src]);
    setMediaUrl('');
    setError('');
  };

  const insertVideo = () => {
    const found = videoEmbed(mediaUrl);
    if (!found) {
      setError('Lien vidéo non reconnu. Utilisez une URL YouTube, Vimeo ou un fichier .mp4.');
      return;
    }
    const inner = found.type === 'iframe'
      ? `<div class="blog-video"><iframe src="${found.src}" title="Vidéo" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe></div>`
      : `<video src="${found.src}" controls playsinline></video>`;
    insertNode(buildFigure(inner));
    setMediaPanel(null);
    setMediaUrl('');
    setError('');
  };

  /** Envoi de plusieurs images d'un coup, insérées les unes après les autres. */
  const uploadImages = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    setError('');
    try {
      for (const file of Array.from(files)) {
        // eslint-disable-next-line no-await-in-loop
        const url = await uploadMediaFile(file, 'blog');
        insertImage(url);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Une image n'a pas pu être envoyée.");
    } finally {
      setUploading(false);
    }
  };

  /** Déplace le média sélectionné (ou le dernier média) vers le haut / le bas. */
  const moveMedia = (dir: -1 | 1) => {
    const el = ref.current;
    if (!el) return;
    const sel = window.getSelection();
    let target: HTMLElement | null = null;
    if (sel && sel.anchorNode) {
      const node = sel.anchorNode.nodeType === 1 ? (sel.anchorNode as HTMLElement) : sel.anchorNode.parentElement;
      target = node?.closest('figure') as HTMLElement | null;
    }
    if (!target) target = el.querySelector('figure:last-of-type');
    if (!target) return;
    const sibling = dir === -1 ? target.previousElementSibling : target.nextElementSibling;
    if (!sibling) return;
    if (dir === -1) target.parentNode?.insertBefore(target, sibling);
    else target.parentNode?.insertBefore(sibling, target);
    emit();
  };

  const setMediaAlignment = (align: Align) => {
    setMediaAlign(align);
    const el = ref.current;
    if (!el) return;
    el.querySelectorAll('figure').forEach((fig) => {
      const f = fig as HTMLElement;
      f.setAttribute('data-align', align);
      f.style.margin = align === 'center' ? '1.5rem auto' : align === 'right' ? '1.5rem 0 1.5rem auto' : '1.5rem auto 1.5rem 0';
    });
    emit();
  };

  const setMediaWidth = (size: MediaSize) => {
    setMediaSize(size);
    const el = ref.current;
    if (!el) return;
    el.querySelectorAll('figure').forEach((fig) => {
      const f = fig as HTMLElement;
      f.setAttribute('data-size', size);
      f.style.width = SIZE_WIDTH[size];
    });
    emit();
  };

  const toolBtn = 'w-8 h-8 flex items-center justify-center rounded-md text-foreground-600 hover:bg-background-200/70 hover:text-foreground-900 transition-colors cursor-pointer flex-shrink-0';

  return (
    <div className="rounded-lg border border-background-200/70 bg-background-50 overflow-hidden">
      {/* Barre d'outils */}
      <div className="flex flex-wrap items-center gap-1 px-2 py-1.5 border-b border-background-200/70 bg-background-100/60">
        <button type="button" onClick={() => formatBlock('p')} title="Paragraphe" className={`${toolBtn} ${activeBlock === 'p' ? 'bg-background-50 text-foreground-900' : ''}`}>
          <i className="ri-paragraph"></i>
        </button>
        <button type="button" onClick={() => formatBlock('h2')} title="Titre" className={`${toolBtn} ${activeBlock === 'h2' ? 'bg-background-50 text-foreground-900' : ''}`}>
          <i className="ri-heading"></i>
        </button>
        <button type="button" onClick={() => formatBlock('h3')} title="Sous-titre" className={`${toolBtn} ${activeBlock === 'h3' ? 'bg-background-50 text-foreground-900' : ''}`}>
          <i className="ri-h-3"></i>
        </button>
        <span className="w-px h-5 bg-background-300/60 mx-0.5"></span>
        <button type="button" onClick={() => exec('bold')} title="Gras" className={toolBtn}><i className="ri-bold"></i></button>
        <button type="button" onClick={() => exec('italic')} title="Italique" className={toolBtn}><i className="ri-italic"></i></button>
        <button type="button" onClick={() => exec('underline')} title="Souligné" className={toolBtn}><i className="ri-underline"></i></button>
        <span className="w-px h-5 bg-background-300/60 mx-0.5"></span>
        <button type="button" onClick={() => exec('justifyLeft')} title="Aligner à gauche" className={toolBtn}><i className="ri-align-left"></i></button>
        <button type="button" onClick={() => exec('justifyCenter')} title="Centrer" className={toolBtn}><i className="ri-align-center"></i></button>
        <button type="button" onClick={() => exec('justifyRight')} title="Aligner à droite" className={toolBtn}><i className="ri-align-right"></i></button>
        <span className="w-px h-5 bg-background-300/60 mx-0.5"></span>
        <button type="button" onClick={() => exec('insertUnorderedList')} title="Liste à puces" className={toolBtn}><i className="ri-list-unordered"></i></button>
        <button type="button" onClick={() => exec('insertOrderedList')} title="Liste numérotée" className={toolBtn}><i className="ri-list-ordered"></i></button>
        <button type="button" onClick={() => formatBlock('blockquote')} title="Citation" className={toolBtn}><i className="ri-double-quotes-l"></i></button>
        <button
          type="button"
          onClick={() => {
            rememberRange();
            const url = window.prompt('Lien (https://…)');
            if (url) exec('createLink', url);
          }}
          title="Insérer un lien"
          className={toolBtn}
        >
          <i className="ri-link"></i>
        </button>
        <span className="w-px h-5 bg-background-300/60 mx-0.5"></span>
        <button
          type="button"
          onClick={() => { rememberRange(); setInserted([]); setMediaPanel('image'); setMediaUrl(''); setError(''); }}
          title="Insérer une image à la position du curseur"
          className={toolBtn}
        >
          <i className="ri-image-add-line"></i>
        </button>
        <button
          type="button"
          onClick={() => { rememberRange(); setMediaPanel('video'); setMediaUrl(''); setError(''); }}
          title="Insérer une vidéo"
          className={toolBtn}
        >
          <i className="ri-video-add-line"></i>
        </button>
        <span className="w-px h-5 bg-background-300/60 mx-0.5"></span>
        <select
          value={mediaSize}
          onChange={(e) => setMediaWidth(e.target.value as MediaSize)}
          title="Taille des médias"
          className="h-8 px-1.5 rounded-md border border-background-200/70 bg-background-50 text-[11px] text-foreground-700 focus:outline-none cursor-pointer"
        >
          <option value="small">Petite</option>
          <option value="medium">Moyenne</option>
          <option value="full">Pleine largeur</option>
        </select>
        <button type="button" onClick={() => setMediaAlignment('left')} title="Médias à gauche" className={toolBtn}><i className="ri-align-left"></i></button>
        <button type="button" onClick={() => setMediaAlignment('center')} title="Médias centrés" className={toolBtn}><i className="ri-align-center"></i></button>
        <button type="button" onClick={() => setMediaAlignment('right')} title="Médias à droite" className={toolBtn}><i className="ri-align-right"></i></button>
        <button type="button" onClick={() => moveMedia(-1)} title="Monter le média" className={toolBtn}><i className="ri-arrow-up-line"></i></button>
        <button type="button" onClick={() => moveMedia(1)} title="Descendre le média" className={toolBtn}><i className="ri-arrow-down-line"></i></button>
      </div>

      {/* Panneau média */}
      {mediaPanel && (
        <div className="px-3 py-3 border-b border-background-200/70 bg-secondary-50/60 space-y-2">
          {mediaPanel === 'image' ? (
            <>
              <div className="flex items-center gap-1.5 text-[11px] text-foreground-600">
                <i className="ri-cursor-line"></i>
                L’image sera insérée à l’endroit où vous avez placé le curseur.
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <label className="flex items-center justify-center gap-2 h-9 px-3 rounded-md bg-primary-500 text-background-50 text-xs font-semibold cursor-pointer whitespace-nowrap hover:bg-primary-600 transition-colors">
                  {uploading ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-upload-2-line"></i>}
                  {uploading ? 'Envoi…' : 'Téléverser'}
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={(e) => void uploadImages(e.target.files)}
                  />
                </label>
                <input
                  type="url"
                  value={mediaUrl}
                  onChange={(e) => setMediaUrl(e.target.value)}
                  placeholder="Ou coller l'URL d'une image"
                  className="flex-1 min-w-0 h-9 px-3 rounded-md border border-background-200/70 bg-background-50 text-xs text-foreground-900 focus:outline-none focus:border-primary-300"
                />
                <button
                  type="button"
                  onClick={() => {
                    if (!/^https?:\/\//i.test(mediaUrl.trim())) { setError('URL d’image invalide.'); return; }
                    insertImage(mediaUrl.trim());
                  }}
                  className="h-9 px-3 rounded-md bg-foreground-950 text-background-50 text-xs font-semibold whitespace-nowrap cursor-pointer hover:bg-foreground-800 transition-colors"
                >
                  Insérer
                </button>
                <button type="button" onClick={() => setMediaPanel(null)} className="h-9 px-3 rounded-md text-xs font-medium text-foreground-500 hover:bg-background-100 cursor-pointer whitespace-nowrap">
                  Terminer
                </button>
              </div>
              {inserted.length > 0 && (
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] text-foreground-500">
                    {inserted.length} image{inserted.length > 1 ? 's' : ''} insérée{inserted.length > 1 ? 's' : ''}
                  </span>
                  {inserted.map((src, index) => (
                    <div key={`${src}-${index}`} className="w-9 h-9 rounded border border-background-200/70 overflow-hidden">
                      <img src={src} alt="" className="w-full h-full object-cover" />
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                type="url"
                value={mediaUrl}
                onChange={(e) => setMediaUrl(e.target.value)}
                placeholder="Lien YouTube, Vimeo ou fichier .mp4"
                className="flex-1 min-w-0 h-9 px-3 rounded-md border border-background-200/70 bg-background-50 text-xs text-foreground-900 focus:outline-none focus:border-primary-300"
              />
              <button type="button" onClick={insertVideo} className="h-9 px-3 rounded-md bg-foreground-950 text-background-50 text-xs font-semibold whitespace-nowrap cursor-pointer hover:bg-foreground-800 transition-colors">
                Insérer la vidéo
              </button>
              <button type="button" onClick={() => setMediaPanel(null)} className="h-9 px-3 rounded-md text-xs font-medium text-foreground-500 hover:bg-background-100 cursor-pointer whitespace-nowrap">
                Annuler
              </button>
            </div>
          )}
          {error && <p className="text-[11px] text-red-600">{error}</p>}
        </div>
      )}

      {/* Zone d'écriture */}
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        aria-label="Contenu de l'article"
        onInput={emit}
        onBlur={() => { rememberRange(); emit(); }}
        onKeyUp={rememberRange}
        onMouseUp={rememberRange}
        onTouchEnd={rememberRange}
        data-placeholder={placeholder || 'Écrivez votre article… Utilisez la barre d’outils pour ajouter des images et des vidéos.'}
        className="blog-editor min-h-[320px] max-h-[60vh] overflow-y-auto px-4 py-3 text-sm leading-relaxed text-foreground-800 focus:outline-none"
      />
    </div>
  );
}